// ============================================================
// 动态路由 store
// 负责：拉 menus → 拆成侧栏树 + 叶子页 → 挂到同一条 Layout
// ============================================================
import { defineStore } from "pinia";
import type { RouteRecordRaw, RouteComponent } from "vue-router";
import type { UserMenuItem } from "@shared/types/user";
import type { SidebarMenuItem } from "@shared/types/sidebarMenu";
import { buildLayoutRoute, splitUserMenus } from "@shared/router/mapUserMenus";
import { APP_LAYOUT_ROUTE_NAME } from "@shared/constants/menu";

interface PermissionState {
    loaded: boolean;
    /** 已注入的顶层 route name（现在只有 AppLayout），登出时 removeRoute */
    routeNames: string[];
    /** 侧栏菜单树 */
    sidebarMenus: SidebarMenuItem[];
    /** Layout 直属业务页（affix / 调试） */
    pageRoutes: RouteRecordRaw[];
    /** 当前用户 type=2 按钮 path */
    buttonPaths: string[];
}

export interface GenerateRoutesOptions {
    layout: RouteComponent;
    fetchUserMenus: () => Promise<UserMenuItem[]>;
}

export const usePermissionStore = defineStore("permission", {
    state: (): PermissionState => ({
        loaded: false,
        routeNames: [],
        sidebarMenus: [],
        pageRoutes: [],
        buttonPaths: [],
    }),

    actions: {
        applyMenus(menus: UserMenuItem[], layout: RouteComponent): RouteRecordRaw[] {
            const { sidebar, pages, buttonPaths } = splitUserMenus(menus);
            const layoutRoute = buildLayoutRoute(pages, layout);
            this.sidebarMenus = sidebar;
            this.pageRoutes = pages;
            this.buttonPaths = buttonPaths;
            this.routeNames = [APP_LAYOUT_ROUTE_NAME];
            this.loaded = true;
            return [layoutRoute];
        },

        async generateRoutes(opts: GenerateRoutesOptions): Promise<RouteRecordRaw[]> {
            const menus = await opts.fetchUserMenus();
            return this.applyMenus(menus, opts.layout);
        },

        /** 登出时调用，外部再 router.removeRoute */
        reset(): string[] {
            const names = [...this.routeNames];
            this.loaded = false;
            this.routeNames = [];
            this.sidebarMenus = [];
            this.pageRoutes = [];
            this.buttonPaths = [];
            return names;
        },
    },
});
